import { skipToken, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { eventKey } from '@/enitites/event/api/keys'
import {
  addGuestToEvent,
  deleteEvent,
  getEvent,
  getEventAttendees,
  getEventEdit,
  getEventFeedbacks,
  getEventPayoutInfo,
  getEventsList,
  getGuestList,
  publishEvent,
  removeGuestFromEvent,
  submitEventFeedback,
  triggerEventPayout,
  updateEvent,
  updateEventFeedback,
} from '@/enitites/event/api/service'
import { IEventFeedbackPostData } from '@/enitites/event/model/types'
import { IEventSearchParams } from '@/enitites/event'
import { computed, ComputedRef } from 'vue'

export const useEventEditQuery = (eventId: number) => {
  return useQuery({
    queryKey: eventKey.edit(eventId),
    queryFn: eventId ? getEventEdit : skipToken,
  })
}

export const useEventQuery = (eventId: number) => {
  return useQuery({
    queryKey: eventKey.details(eventId),
    queryFn: eventId ? getEvent : skipToken,
  })
}

export const useEventUpdateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateEvent,
    onSuccess: (data) => {
      queryClient.setQueryData(eventKey.edit(data.id), data)
    },
  })
}

export const useEventDeleteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteEvent,
    onSuccess: (_, eventId) => {
      queryClient.removeQueries({
        queryKey: eventKey.edit(eventId),
      })
    },
  })
}

export const useEventPublishMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: publishEvent,
    onSuccess: (_, eventId) => {
      queryClient.invalidateQueries({
        queryKey: eventKey.edit(eventId),
      })
    },
  })
}

export const useEventGuestsQuery = (eventId: number) => {
  return useQuery({
    queryKey: eventKey.guestList(eventId),
    queryFn: eventId ? getGuestList : skipToken,
  })
}

export const useAddGuestToEventMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: addGuestToEvent,
    onSuccess: (_, variables) => {
      const { eventId } = variables
      queryClient.invalidateQueries({
        queryKey: eventKey.guestList(eventId),
      })
    },
  })
}

export const useRemoveGuestFromEventMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: removeGuestFromEvent,
    onSuccess: (_, variables) => {
      const { eventId } = variables
      queryClient.invalidateQueries({
        queryKey: eventKey.guestList(eventId),
      })
    },
  })
}

export const useEventPayoutInfoQuery = (eventId: number) => {
  return useQuery({
    queryKey: eventKey.payoutInfo(eventId),
    queryFn: eventId ? getEventPayoutInfo : skipToken,
  })
}

export const useTriggerEventPayoutMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: triggerEventPayout,
    onSuccess: (_, eventId) => {
      queryClient.invalidateQueries({
        queryKey: eventKey.payoutInfo(eventId),
      })
    },
    onError: (_, eventId) => {
      queryClient.invalidateQueries({
        queryKey: eventKey.payoutInfo(eventId),
      })
    },
  })
}

export const useEventFeedbackMutation = (eventId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ data, isUpdate }: { data: IEventFeedbackPostData; isUpdate: boolean }) =>
      isUpdate ? updateEventFeedback({ eventId, data }) : submitEventFeedback({ eventId, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventKey.details(eventId) })
      queryClient.invalidateQueries({ queryKey: eventKey.feedbackList(eventId) })
    },
  })
}

export const useEventAttendeesQuery = (eventId: number) => {
  return useQuery({
    queryKey: eventKey.attendees(eventId),
    queryFn: eventId ? getEventAttendees : skipToken,
  })
}

export const useEventFeedbacksQuery = (eventId: number, enabled: ComputedRef<boolean>) => {
  return useQuery({
    queryKey: eventKey.feedbackList(eventId),
    queryFn: eventId ? getEventFeedbacks : skipToken,
    enabled,
  })
}

export const useEventsListQuery = (params: ComputedRef<IEventSearchParams>) => {
  return useInfiniteQuery({
    queryKey: computed(() => eventKey.eventsList(params)),
    // @ts-expect-error ignore
    queryFn: getEventsList,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _, lastPageParam) => {
      if (lastPage.length === params.value.pageSize) {
        return Number(lastPageParam) + 1
      }
      return undefined
    },
    staleTime: 5 * 60 * 1000,
  })
}
